import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Play, Settings, Check, UserPlus, Trash2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import toast from 'react-hot-toast';
import { ConfirmDialog } from '../components/Modal';
import UpgradeModal from '../components/UpgradeModal';

const Profiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchProfiles();
    fetchStats();
  }, []);

  // Refresh stats when navigating to this page (handles returning from other pages)
  useEffect(() => {
    if (location.pathname === '/profiles') {
      fetchStats();
    }
  }, [location.pathname]);

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/profiles');
      if (response.data.success) {
        setProfiles(response.data.profiles);
      }
    } catch (error) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/users/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const openDeleteConfirm = (profile) => {
    setDeleteTarget(profile);
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const response = await api.delete(`/profiles/${deleteTarget.id}`);
      if (response.data.success) {
        toast.success('Profile deleted');
        fetchProfiles();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete profile');
    } finally {
      setDeleting(false);
      setShowConfirmModal(false);
      setDeleteTarget(null);
    }
  };

  const getAvatarColor = (name) => {
    const colors = [
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-pink-400 to-pink-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
      'from-yellow-400 to-yellow-600',
    ];
    return colors[name.charCodeAt(0) % colors.length];
  };

  if (loading) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-8 flex-1">
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
            My Story{' '}
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Profiles
            </span>
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Manage all your children's story profiles in one place. Create unique characters and watch their adventures come to life!
          </p>
        </div>

        {profiles.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-purple-100">
              <div className="text-2xl font-bold text-purple-600">{stats.totalProfiles || 0}</div>
              <div className="text-sm text-gray-600">Active Profiles</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-purple-100">
              <div className="text-2xl font-bold text-green-600">{stats.totalStories || 0}</div>
              <div className="text-sm text-gray-600">Stories Created</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-purple-100">
              <div className="text-2xl font-bold text-blue-600">{stats.favoriteStories || 0}</div>
              <div className="text-sm text-gray-600">Favorite Stories</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-purple-100">
              <div className="text-2xl font-bold text-pink-600">{stats.thisMonth || 0}</div>
              <div className="text-sm text-gray-600">This Month</div>
            </div>
          </div>
        )}

        {profiles.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-4">
                <UserPlus className="h-16 w-16 text-purple-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Create Your First Story Profile</h2>
              <p className="text-gray-600 mb-6">
                Start your magical storytelling journey by creating a personalized profile for your child.
              </p>
              <Link
                to="/profiles/create"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Your First Profile
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/profiles/create"
              className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-purple-500 hover:bg-purple-50 transition-all cursor-pointer"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-10 w-10 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Create New Profile</h3>
              <p className="text-gray-600 text-sm">Add another child to your story collection</p>
            </Link>

            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="relative mb-4">
                  <div
                    className={`w-20 h-20 mx-auto bg-gradient-to-br ${getAvatarColor(
                      profile.first_name
                    )} rounded-full flex items-center justify-center text-white text-2xl font-bold`}
                  >
                    {profile.first_name[0].toUpperCase()}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/stories?profile=${profile.id}&autoGenerate=true`);
                    }}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer"
                    title="Quick create story"
                  >
                    <Check className="h-3 w-3 text-white" />
                  </button>
                </div>
                <h3 className="text-lg font-bold text-gray-900 text-center mb-1">
                  {profile.first_name}
                </h3>
                <p className="text-gray-600 text-sm text-center mb-4">
                  Age {profile.age} • {profile.themes?.[0] || 'Adventure'}
                </p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-purple-600">{profile.story_count || 0}</div>
                    <div className="text-xs text-gray-600">Stories</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-pink-600">{profile.favorite_count || 0}</div>
                    <div className="text-xs text-gray-600">Favorites</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/stories?profile=${profile.id}&autoGenerate=true`)}
                    className="flex-1 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
                  >
                    <Play className="h-4 w-4 mr-1 inline" />
                    Create Story
                  </button>
                  <button
                    onClick={() => navigate(`/profiles/${profile.id}`)}
                    className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(profile)}
                    className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    title="Delete profile"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />

      {showConfirmModal && deleteTarget && (
        <ConfirmDialog
          title="Delete Profile"
          description={`Are you sure you want to delete ${deleteTarget.first_name}? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting...' : 'Yes, Delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTarget(null);
          }}
        />
      )}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        message={upgradeMessage}
        reason="FREE_ACCOUNT_LIMIT"
      />
    </div>
  );
};

export default Profiles;

